import { VoiceBindingDTO } from '../ports/types';
import { AudioMixer } from './AudioMixer';

export type VoiceActionType = 'tap' | 'drag_release' | 'wobble';

export interface VoiceSlotSummaryDTO {
  actionType: VoiceActionType;
  isBound: boolean;
  name?: string;
  durationSec?: number;
}

export interface VoiceBindingRecord {
  actionType: VoiceActionType;
  name: string;
  audioBuffer: AudioBuffer;
  durationSec: number;
  fileSize: number;
  registeredAt: number;
}

export interface CustomVoiceManagerOptions {
  mixer: AudioMixer;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5.0 MB

const ALLOWED_MIME_TYPES = new Set([
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/mpeg',
  'audio/mp3',
  'audio/aac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/ogg',
  'audio/webm',
]);

const ALLOWED_EXTENSIONS = ['.wav', '.mp3', '.aac', '.m4a', '.ogg', '.webm', '.weba'];

/**
 * カスタムボイス紐付けコンポーネント (CustomVoiceComponent)
 * 手持ち音声ファイル（WAV/MP3/AAC等）の安全な取り込み、バリデーション、
 * 3スロット（tap, drag_release, wobble）へのバインド管理、ゼロ遅延再生を担う。
 */
export class CustomVoiceManager {
  private mixer: AudioMixer;
  private slots: Map<VoiceActionType, VoiceBindingRecord> = new Map();
  private currentPlayingSources: Map<VoiceActionType, AudioBufferSourceNode> = new Map();

  constructor(options: CustomVoiceManagerOptions) {
    this.mixer = options.mixer;
  }

  /**
   * 音声ファイルをスロットにバインド登録
   */
  public async bindVoice(dto: VoiceBindingDTO): Promise<{ success: boolean; errorMessage?: string }> {
    if (this.mixer.getEdition() === 'web_free') {
      return {
        success: false,
        errorMessage: 'Custom voice feature is locked in Web free edition.',
      };
    }

    if (!dto.audioFile) {
      return { success: false, errorMessage: 'No audio file provided.' };
    }

    // 1. ファイルサイズチェック (<= 5MB)
    if (dto.audioFile.size > MAX_FILE_SIZE_BYTES) {
      return {
        success: false,
        errorMessage: `File size exceeds 5.0 MB limit (${(dto.audioFile.size / (1024 * 1024)).toFixed(2)} MB).`,
      };
    }

    // 2. MIMEタイプおよび拡張子チェック
    const isValidFormat = this.validateFormat(dto.audioFile);
    if (!isValidFormat) {
      return {
        success: false,
        errorMessage: 'Invalid audio format. Allowed: WAV, MP3, AAC, M4A, OGG, WebM.',
      };
    }

    // 3. AudioContext & デコード処理
    const audioContext = this.mixer.getAudioContext();
    if (!audioContext) {
      return {
        success: false,
        errorMessage: 'AudioContext is not available.',
      };
    }

    try {
      const arrayBuffer = await dto.audioFile.arrayBuffer();
      // decodeAudioData の実行 (レガシーブラウザコールバックとPromise両対応)
      const audioBuffer = await new Promise<AudioBuffer>((resolve, reject) => {
        try {
          const promise = audioContext.decodeAudioData(
            arrayBuffer.slice(0),
            (decoded) => resolve(decoded),
            (err) => reject(err)
          );
          if (promise && typeof promise.then === 'function') {
            promise.then(resolve).catch(reject);
          }
        } catch (e) {
          reject(e);
        }
      });

      // 4. スロット登録
      const record: VoiceBindingRecord = {
        actionType: dto.actionType,
        name: dto.name || 'Custom Voice',
        audioBuffer,
        durationSec: audioBuffer.duration,
        fileSize: dto.audioFile.size,
        registeredAt: Date.now(),
      };

      this.slots.set(dto.actionType, record);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        errorMessage: `Failed to decode audio file: ${(err as Error).message || String(err)}`,
      };
    }
  }

  private validateFormat(file: File | Blob): boolean {
    const type = file.type ? file.type.toLowerCase() : '';
    if (type && ALLOWED_MIME_TYPES.has(type)) {
      return true;
    }

    // Blob または File の名前から拡張子をフォールバック判定
    const name = (file as File).name;
    if (name) {
      const lower = name.toLowerCase();
      return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    }

    // MIME typeが空かつ名前なしの場合は念のため型指定がaudio/で始まっていれば許容
    return type.startsWith('audio/');
  }

  /**
   * 指定スロットの音声紐付けを解除
   */
  public unbindVoice(actionType: VoiceActionType): void {
    this.stopVoice(actionType);
    this.slots.delete(actionType);
  }

  /**
   * 指定スロットに音声が登録されているか判定
   */
  public hasVoice(actionType: VoiceActionType): boolean {
    return this.slots.has(actionType);
  }

  /**
   * 全スロットの登録状況サマリーを取得
   */
  public getSlotsSummary(): VoiceSlotSummaryDTO[] {
    const actionTypes: VoiceActionType[] = ['tap', 'drag_release', 'wobble'];
    return actionTypes.map((actionType) => {
      const record = this.slots.get(actionType);
      if (record) {
        return {
          actionType,
          isBound: true,
          name: record.name,
          durationSec: record.durationSec,
        };
      }
      return {
        actionType,
        isBound: false,
      };
    });
  }

  /**
   * スロットに登録されたカスタムボイスを再生
   * @param actionType アクション種別
   * @param onEnded 再生終了時コールバック（ダッキング復帰用）
   * @returns 再生を開始できたかどうか
   */
  public playVoice(actionType: VoiceActionType, onEnded?: () => void): boolean {
    if (this.mixer.getEdition() === 'web_free') {
      return false;
    }

    const record = this.slots.get(actionType);
    if (!record) {
      return false;
    }

    const audioContext = this.mixer.getAudioContext();
    const destinationNode = this.mixer.getVoiceGainNode();
    if (!audioContext || !destinationNode) {
      return false;
    }

    void this.mixer.ensureRunning();

    // 同一スロットで再生中の音声があれば停止
    this.stopVoice(actionType);

    try {
      const source = audioContext.createBufferSource();
      source.buffer = record.audioBuffer;
      source.connect(destinationNode);

      source.onended = () => {
        if (this.currentPlayingSources.get(actionType) === source) {
          this.currentPlayingSources.delete(actionType);
        }
        try {
          source.disconnect();
        } catch {
          // ignore
        }
        onEnded?.();
      };

      const now = audioContext.currentTime ?? 0;
      source.start(now);
      this.currentPlayingSources.set(actionType, source);
      return true;
    } catch (err) {
      console.warn('[CustomVoiceManager] Failed to play voice:', err);
      return false;
    }
  }

  /**
   * 再生中のカスタムボイスを停止
   */
  public stopVoice(actionType?: VoiceActionType): void {
    if (actionType) {
      const source = this.currentPlayingSources.get(actionType);
      if (source) {
        try {
          source.stop();
          source.disconnect();
        } catch {
          // ignore
        }
        this.currentPlayingSources.delete(actionType);
      }
    } else {
      for (const [key, source] of this.currentPlayingSources.entries()) {
        try {
          source.stop();
          source.disconnect();
        } catch {
          // ignore
        }
      }
      this.currentPlayingSources.clear();
    }
  }

  public getBoundRecord(actionType: VoiceActionType): VoiceBindingRecord | undefined {
    return this.slots.get(actionType);
  }
}
